import { Injectable, inject } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router } from '@angular/router';

/**
 * POV Redirect Guard
 * 
 * Redirects old format routes (e.g., /facility-usage-logs/:id) to pov-scoped format
 * (/:user_pov/:id/facility-usage-logs) based on user_pov value
 * Supports both "parking" and "organization" POVs dynamically
 */
@Injectable({
  providedIn: 'root'
})
export class ParkingRedirectGuard implements CanActivate {
  private router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): boolean {
    const userPov = sessionStorage.getItem('user_pov');
    const parkingId = sessionStorage.getItem('parking_id');
    const organizationId = sessionStorage.getItem('organization_id');

    // Get the dynamic prefix from user_pov - NEVER hardcode, always use user_pov value
    const dynamicPov = userPov ? userPov.toLowerCase() : 'organization';

    // Only redirect if we have a valid ID (parkingId or organizationId)
    const routePath = route.routeConfig?.path || '';
    const routeParams = route.params;
    const id = routeParams['id'] || routeParams['parkingId'] || routeParams['orgId'];

    // Check if this is a settings route by checking parent route
    const parentRoute = route.parent;
    const isSettingsRoute = parentRoute?.routeConfig?.path === 'settings' ||
      routePath.includes('settings') ||
      (parentRoute && parentRoute.url.some((segment: { path: string }) => segment.path === 'settings'));

    // List of regular routes that should be redirected to pov-scoped format
    // Key: route path pattern, Value: page name for new format
    const regularRoutesToRedirect: { [key: string]: string } = {
      'facility-usage-logs/:id': 'facility-usage-logs',
      'facility-activities/:id': 'facility-activities',
      'facility-activities/:parkingId': 'facility-activities',
      'vehicle-sessions/:id': 'vehicle-sessions',
      'session/:id': 'session',
      'session/:parkingId': 'session',
      'customers/:id': 'customers',
      'customers/:parkingId': 'customers',
      'customer/:id': 'customer',
      'passes/:id': 'passes',
      'passes/:parkingId': 'passes',
      'reports/:id': 'reports',
      'reports/:parkingId': 'reports',
      'reservations/:id': 'reservations',
      'reservations/:parkingId': 'reservations',
      'reservation/:id': 'reservation',
      'facilities/:id': 'facilities',
      'facilities/:parkingId': 'facilities',
      'linked-vehicles/:id': 'linked-vehicles',
      'data-exports/:id': 'data-exports',
      'data-import/:id': 'data-import',
      'dataimport/:id': 'data-import',
      'data-export/:id': 'data-export',
      'dataexport/:id': 'data-export',
      'ai/:id': 'ai',
      'sistem-ai-insight/:id': 'sistem-ai-insight',
      'billing/:id': 'billing',
      'billing-invoice/:id': 'billing-invoice',
      'parking-lots/:id': 'parking-lots',
    };

    // List of settings sub-routes that should be redirected
    const settingsRoutesToRedirect: { [key: string]: string } = {
      'org-profile/:id': 'org-profile',
      'organization-profile/:id': 'organization-profile',
      'parking-profile/:parkingId': 'parking-profile',
      'team/:id': 'team',
      'passes/:id': 'passes',
      'facilities/:id': 'facilities',
      'integrations/:id': 'integrations',
      'devices/:id': 'devices',
      'payment-gateway/:id': 'payment-gateway',
      'taxes/:id': 'taxes',
      'regions/:id': 'regions',
      'language/:id': 'language',
      'media-branding/:id': 'media-branding',
      'data-import/:id': 'data-import',
      'dataimport/:id': 'data-import',
      'data-export/:id': 'data-export',
      'dataexport/:id': 'data-export',
      'vehicle-rate/:id': 'vehicle-rate',
      'vehicle-rates/:id': 'vehicle-rate',
      'pass-rates/:id': 'passes',
      'facility-rates/:id': 'facilities',
      'facility-setup/:id': 'facilities',
      'hardware-integration/:id': 'integrations',
      'anpr-module/:id': 'integrations',
      'payment-options/:id': 'payment-gateway',
      'thermal-printer/:id': 'thermal-printer',
    };

    // Choose the appropriate redirect map based on whether this is a settings route
    const routesToRedirect = isSettingsRoute ? settingsRoutesToRedirect : regularRoutesToRedirect;

    // Check if this route should be redirected
    if (routesToRedirect[routePath] && id) {
      // Check if route uses parkingId parameter (for parking-profile routes)
      const hasParkingIdParam = routePath.includes(':parkingId');

      // Determine target ID and prefix - ALWAYS use dynamic user_pov, NEVER hardcode
      let finalTargetId: string | null = null;
      let finalUrlPrefix = dynamicPov; // Always use dynamic user_pov value

      // For settings routes, try to match ID with either organizationId or parkingId
      if (isSettingsRoute) {
        // First check if ID matches parkingId
        if (parkingId && id === parkingId) {
          finalTargetId = parkingId;
          finalUrlPrefix = dynamicPov; // Use dynamic user_pov value
        }
        // Then check if ID matches organizationId
        else if (organizationId && id === organizationId) {
          finalTargetId = organizationId;
          finalUrlPrefix = dynamicPov; // Use dynamic user_pov value
        }
        // If no match, use organizationId as default if available
        else if (organizationId) {
          finalTargetId = organizationId;
          finalUrlPrefix = dynamicPov; // Use dynamic user_pov value
        }
        // Last resort: use parkingId if available
        else if (parkingId) {
          finalTargetId = parkingId;
          finalUrlPrefix = dynamicPov; // Use dynamic user_pov value
        }
      } else {
        // For regular routes, use dynamic user_pov value
        if (hasParkingIdParam && parkingId && id === parkingId) {
          finalTargetId = parkingId;
          finalUrlPrefix = dynamicPov; // Use dynamic user_pov value
        } else if (parkingId && id === parkingId) {
          finalTargetId = parkingId;
          finalUrlPrefix = dynamicPov; // Use dynamic user_pov value
        } else if (organizationId && id === organizationId) {
          finalTargetId = organizationId;
          finalUrlPrefix = dynamicPov; // Use dynamic user_pov value
        } else if (organizationId) {
          finalTargetId = organizationId;
          finalUrlPrefix = dynamicPov; // Use dynamic user_pov value
        } else if (parkingId) {
          finalTargetId = parkingId;
          finalUrlPrefix = dynamicPov; // Use dynamic user_pov value
        }
      }

      if (finalTargetId) {
        const redirectPath = routesToRedirect[routePath];

        if (isSettingsRoute) {
          // For settings routes, construct as /:pov/:id/settings/:subRoute
          const newPath = `/${finalUrlPrefix}/${finalTargetId}/settings/${redirectPath}`;
          console.log('ParkingRedirectGuard: Redirecting settings route', routePath, 'to', newPath);
          this.router.navigate([newPath], { replaceUrl: true });
          return false; // Prevent activation of old route
        } else {
          // For regular routes, construct as /:pov/:id/:pageName
          const newPath = `/${finalUrlPrefix}/${finalTargetId}/${redirectPath}`;
          console.log('ParkingRedirectGuard: Redirecting', routePath, 'to', newPath);
          this.router.navigate([newPath], { replaceUrl: true });
          return false; // Prevent activation of old route
        }
      }
    }

    return true; // Allow activation
  }
}

